// Private-by-default records (Kelly, Oct 26 2026): every contact, pipeline buyer/seller record and
// transaction has one owner (ownerUid) and an optional sharedWith map { uid: true }.
// Only the owner, the people it is shared with, and admins (oversight) can see it.
//   Share    = the record also shows in the other person's own lists; both can see and work it.
//   Transfer = ownership moves; the previous owner loses access (unless they tick "keep me on it").
// The database cannot filter a list by owner, so each user has an index of what they can see:
//   crm/access/{uid}/{kind}/{recordId} = true     (kind: contacts | sellerInterests | buyerInterests)
// Non-admins load their lists from that index, one record at a time. Admins read the whole path.
// Transactions keep their existing home (users/{owner}/transactions/{id}); sharing for them lives on
// transactionIndex/{id}/sharedWith (see transactionService).
import { ref, onValue, get, update, push, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import { assignedAgentOf, campaignTasksToMove } from '../shared/leadPermissions';

export const KIND_PATH = { contacts: 'crm/contacts', sellerInterests: 'crm/sellerInterests', buyerInterests: 'crm/buyerInterests' };
export const KIND_LABEL = { contacts: 'contact', sellerInterests: 'seller record', buyerInterests: 'buyer record', transactions: 'transaction' };

// Owner of a record, falling back to the older fields for records made before ownerUid existed.
export const ownerOf = (rec) => {
  if (!rec) return '';
  if (rec.ownerUid) return rec.ownerUid;
  if (rec.ownerType === 'agent' && rec.ownerId) return rec.ownerId;
  return rec.assignedToId || rec.assignedAgentId || rec.createdBy || '';
};
export const sharedUids = (rec) => Object.keys((rec && rec.sharedWith) || {}).filter((u) => rec.sharedWith[u]);
export const isOwner = (rec, uid) => !!uid && ownerOf(rec) === uid;
export const isSharedWith = (rec, uid) => !!uid && !!(rec && rec.sharedWith && rec.sharedWith[uid]);
export const canSeeRecord = (rec, uid, isAdmin) => !!rec && (!!isAdmin || isOwner(rec, uid) || isSharedWith(rec, uid));
// Shared people can work the record (notes, calls, edits); only the owner or an admin can share,
// transfer or delete it.
export const canWorkRecord = canSeeRecord;
export const canManageRecord = (rec, uid, isAdmin) => !!rec && (!!isAdmin || isOwner(rec, uid));

// Fields that make a brand-new record private to its owner. Pass `from` (for example the contact a
// pipeline record came from) to carry its sharing over.
export const newOwnership = (ownerUid, from = null) => {
  const sharedWith = { ...((from && from.sharedWith) || {}) };
  const fromOwner = from ? ownerOf(from) : '';
  if (fromOwner && fromOwner !== ownerUid) sharedWith[fromOwner] = true;
  delete sharedWith[ownerUid];
  return { ownerUid, ...(Object.keys(sharedWith).length ? { sharedWith } : {}) };
};

// Index writes (multi-path update entries) so owner + shared users see the record in their lists.
export const indexOps = (kind, id, rec, prev = null) => {
  const ops = {};
  if (!KIND_PATH[kind] || !id) return ops;
  const now = new Set([ownerOf(rec), ...sharedUids(rec)].filter(Boolean));
  const before = prev ? new Set([ownerOf(prev), ...sharedUids(prev)].filter(Boolean)) : new Set();
  now.forEach((u) => { ops[`crm/access/${u}/${kind}/${id}`] = true; });
  before.forEach((u) => { if (!now.has(u)) ops[`crm/access/${u}/${kind}/${id}`] = null; });
  return ops;
};

// Live list of records this user can see. Admins: the whole path. Everyone else: their index.
export const watchAccessible = (kind, uid, isAdmin, cb) => {
  const path = KIND_PATH[kind];
  const listOf = (val) => Object.entries(val || {}).map(([id, v]) => ({ id, ...v }));
  if (isAdmin) {
    // Admins read the whole collection. If that read is refused (e.g. the live rules lack the
    // collection-level admin read), fall back to the admin's own access index instead of showing
    // an empty page; records owned/assigned/shared with them still appear.
    let off = () => {};
    let fellBack = false;
    off = onValue(ref(database, path), (s) => cb(listOf(s.val())), (err) => {
      if (fellBack) return;
      fellBack = true;
      console.warn(`Full ${kind} read refused; using your access list instead.`, err && err.message);
      off = watchAccessible(kind, uid, false, cb);
    });
    return () => off();
  }
  if (!uid) { cb([]); return () => {}; }
  const subs = new Map(); const rows = new Map();
  let ready = false;
  const emit = () => { if (ready) cb([...rows.values()].filter(Boolean)); };
  const offIndex = onValue(ref(database, `crm/access/${uid}/${kind}`), (snap) => {
    const ids = new Set(Object.keys(snap.val() || {}));
    subs.forEach((off, id) => { if (!ids.has(id)) { off(); subs.delete(id); rows.delete(id); } });
    let pending = [...ids].filter((id) => !subs.has(id)).length;
    ready = pending === 0;
    ids.forEach((id) => {
      if (subs.has(id)) return;
      let first = true;
      subs.set(id, onValue(ref(database, `${path}/${id}`), (s) => {
        const v = s.val();
        rows.set(id, v ? { id, ...v } : null);
        if (first) { first = false; pending -= 1; if (pending <= 0) ready = true; }
        emit();
      }, () => { rows.set(id, null); if (first) { first = false; pending -= 1; if (pending <= 0) ready = true; } emit(); }));
    });
    emit();
  }, () => cb([]));
  return () => { offIndex(); subs.forEach((off) => off()); };
};

// One-shot version (for services). Returns [{ id, ...record }].
export const getAccessible = async (kind, uid, isAdmin) => {
  const path = KIND_PATH[kind];
  if (isAdmin) {
    try { return Object.entries((await get(ref(database, path))).val() || {}).map(([id, v]) => ({ id, ...v })); } catch (e) { return getAccessible(kind, uid, false); }
  }
  if (!uid) return [];
  const ids = Object.keys((await get(ref(database, `crm/access/${uid}/${kind}`))).val() || {});
  const recs = await Promise.all(ids.map((id) => get(ref(database, `${path}/${id}`)).then((s) => (s.exists() ? { id, ...s.val() } : null)).catch(() => null)));
  return recs.filter(Boolean);
};

// Timeline entry about sharing/transfer. Contacts log on their own timeline; pipeline records log on
// their linked contact (if any) and in their own accessLog.
const logAccess = (kind, id, rec, text, actorUid) => {
  const at = new Date().toISOString();
  const ops = {};
  const entry = { type: 'access', text, at, by: actorUid || null };
  if (kind === 'contacts') ops[`crm/activity/${id}/${push(ref(database, `crm/activity/${id}`)).key}`] = entry;
  else {
    ops[`${KIND_PATH[kind]}/${id}/accessLog/${push(ref(database, 'x')).key}`] = entry;
    if (rec && rec.contactId) ops[`crm/activity/${rec.contactId}/${push(ref(database, 'x')).key}`] = { ...entry, text: `${text} (${KIND_LABEL[kind]})` };
  }
  return ops;
};

export class AccessError extends Error {}

// Pipeline records also note the change on the linked contact's timeline. That contact may belong to
// someone else, so it is written separately and a failure there never blocks the share/transfer.
const commit = async (kind, ops) => {
  const side = {};
  if (kind !== 'contacts') Object.keys(ops).filter((k) => k.startsWith('crm/activity/')).forEach((k) => { side[k] = ops[k]; delete ops[k]; });
  await update(ref(database), ops);
  if (Object.keys(side).length) await update(ref(database), side).catch((e) => console.warn('Timeline note skipped', e));
};

// Share with (or stop sharing with) team members. add/remove are arrays of uids.
export const setSharing = async (kind, rec, { add = [], remove = [] }, actorUid, isAdmin, nameOf = (u) => u) => {
  if (!canManageRecord(rec, actorUid, isAdmin)) throw new AccessError('Only the owner (or an admin) can change who this is shared with.');
  const owner = ownerOf(rec);
  const next = { ...(rec.sharedWith || {}) };
  add.filter((u) => u && u !== owner).forEach((u) => { next[u] = true; });
  remove.forEach((u) => { delete next[u]; });
  const path = `${KIND_PATH[kind]}/${rec.id}`;
  const ops = { [`${path}/sharedWith`]: Object.keys(next).length ? next : null, [`${path}/updatedAt`]: new Date().toISOString() };
  if (!rec.ownerUid && owner) ops[`${path}/ownerUid`] = owner;
  Object.assign(ops, indexOps(kind, rec.id, { ...rec, ownerUid: owner, sharedWith: next }, rec));
  const added = add.filter((u) => u && u !== owner && !(rec.sharedWith || {})[u]);
  const removed = remove.filter((u) => (rec.sharedWith || {})[u]);
  const parts = [];
  if (added.length) parts.push(`Shared with ${added.map(nameOf).join(', ')}`);
  if (removed.length) parts.push(`Stopped sharing with ${removed.map(nameOf).join(', ')}`);
  if (parts.length) Object.assign(ops, logAccess(kind, rec.id, rec, parts.join('. '), actorUid));
  await commit(kind, ops);
  return next;
};

// Owner fields per kind, so older screens that still read the legacy fields stay correct.
const legacyOwnerFields = (kind, uid) => (kind === 'contacts'
  ? { ownerType: 'agent', ownerId: uid, assignedToId: uid }
  : { assignedAgentId: uid });

// Transfer ownership. Default: the previous owner loses access (keepPrevious = false).
export const transferRecord = async (kind, rec, toUid, actorUid, isAdmin, { keepPrevious = false, nameOf = (u) => u } = {}) => {
  if (!canManageRecord(rec, actorUid, isAdmin)) throw new AccessError('Only the owner (or an admin) can transfer this.');
  if (!toUid) throw new AccessError('Choose who to transfer it to.');
  const prevOwner = ownerOf(rec);
  if (toUid === prevOwner) return;
  const sharedWith = { ...(rec.sharedWith || {}) };
  delete sharedWith[toUid];
  if (keepPrevious && prevOwner) sharedWith[prevOwner] = true;
  // The agent who entered a lead stays its owner: they keep seeing it after handing off the assignment.
  if (kind === 'contacts' && rec.createdBy && rec.createdBy !== toUid && rec.leadGeneratedBy !== 'team' && !rec.boldtrailId && !rec.importedBy) sharedWith[rec.createdBy] = true;
  const path = `${KIND_PATH[kind]}/${rec.id}`;
  const at = new Date().toISOString();
  const ops = { [`${path}/ownerUid`]: toUid, [`${path}/sharedWith`]: Object.keys(sharedWith).length ? sharedWith : null, [`${path}/updatedAt`]: at, [`${path}/transferredAt`]: at, [`${path}/transferredFrom`]: prevOwner || null };
  Object.entries(legacyOwnerFields(kind, toUid)).forEach(([k, v]) => { ops[`${path}/${k}`] = v; });
  Object.assign(ops, indexOps(kind, rec.id, { ownerUid: toUid, sharedWith }, rec));
  Object.assign(ops, logAccess(kind, rec.id, rec, `Transferred from ${nameOf(prevOwner) || 'unassigned'} to ${nameOf(toUid)}${keepPrevious ? ` (${nameOf(prevOwner)} kept on it)` : ''}`, actorUid));
  await commit(kind, ops);
  // Future campaign tasks follow the new assigned agent (completed ones stay with whoever did them).
  if (kind === 'contacts') await moveCampaignTasks(rec, toUid).catch((e) => console.warn('Could not move campaign tasks', e && e.message));
};
export const moveCampaignTasks = async (rec, toUid) => {
  const fromUid = assignedAgentOf(rec, null);
  if (!fromUid || !toUid || fromUid === toUid) return 0;
  const snap = await get(ref(database, `users/${fromUid}/tasks`));
  const tasks = snap.exists() ? snap.val() : {};
  const ids = campaignTasksToMove(tasks, rec.id);
  if (!ids.length) return 0;
  const at = new Date().toISOString();
  const ops = {};
  ids.forEach((id) => { ops[`users/${toUid}/tasks/${id}`] = { ...tasks[id], assignee: toUid, updatedAt: at, movedFrom: fromUid }; ops[`users/${fromUid}/tasks/${id}`] = null; });
  await update(ref(database), ops);
  return ids.length;
};

// Write a brand-new record with its ownership and index in one update. Returns the new id.
export const createOwned = async (kind, data, ownerUid, actorUid, from = null) => {
  const path = KIND_PATH[kind];
  const id = push(ref(database, path)).key;
  const at = new Date().toISOString();
  const rec = { ...data, ...newOwnership(ownerUid, from), createdAt: data.createdAt || at, updatedAt: at, createdBy: actorUid || null };
  await update(ref(database), { [`${path}/${id}`]: rec, ...indexOps(kind, id, rec) });
  return id;
};
// Adds index entries for an existing record (used after older code saved it).
export const ensureIndexed = (kind, id, rec) => {
  const owner = ownerOf(rec);
  if (!owner || !id) return Promise.resolve();
  const ops = indexOps(kind, id, { ...rec, ownerUid: owner });
  if (!rec.ownerUid) ops[`${KIND_PATH[kind]}/${id}/ownerUid`] = owner;
  return update(ref(database), ops);
};
